/**
 * Florida State-Specific Diagnostic Questions
 * All questions reference actual FL regulations, forms, and requirements
 * Regulatory Body: Agency for Health Care Administration (AHCA)
 * Reference: Florida Administrative Code 59A-8, Chapter 400 F.S.
 * 
 * CORRECTED VERSION - Key fixes applied:
 * - FL-E1: Training hours corrected (40 hours state / 75 hours Medicare-Medicaid)
 * - FL-A4: Citation updated from repealed 59A-8.004 to 59A-8.0095
 * - FL-A11: Surety bond requirements clarified (conditional, not universal)
 * - FL-E3: Annual in-service clarified for Medicare/Medicaid agencies
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const FLORIDA_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'FL-A1',
    itemId: 'A1',
    stateCode: 'FL',
    title: 'AHCA Home Health Agency License',
    description: 'Florida requires AHCA licensure for home health agencies under Chapter 400 F.S.',
    severity: 'high',
    regulationCode: '59A-8.003',
    regulationName: 'Licensing',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com/MCHQ/Home_Care/HHA',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from AHCA?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Florida HHA licenses must be renewed annually. Renewal applications due 60 days before expiration.',
      },
      {
        id: 'q2',
        question: 'What is your AHCA license expiration date?',
        inputType: 'date',
        helpText: 'Found on your AHCA license certificate. Renewal due 60 days before expiration.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Is an RN available 24/7 for consultation as required for skilled services?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, 24/7 RN availability' },
          { value: 'limited', label: 'RN available during business hours only' },
          { value: 'no', label: 'No RN on staff' },
          { value: 'na', label: 'N/A - non-skilled services only' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no license OR no RN for skilled services
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      // AT_RISK: limited RN availability
      { condition: 'equals', questionId: 'q3', value: 'limited', result: 'AT_RISK' },
      // COMPLIANT first: license valid 60+ days (check larger values first!)
      { condition: 'daysFuture', questionId: 'q2', value: 60, result: 'COMPLIANT' },
      // AT_RISK: license expiring within 30-60 days
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      // NON_COMPLIANT: license expiring within 30 days or expired
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your AHCA Home Health Agency license is current per 59A-8.003 F.A.C.',
      atRisk: 'Your license expires within 60 days OR RN availability is limited. Florida requires 24/7 RN availability for skilled services and renewal applications 60 days before expiration.',
      nonCompliant: 'Operating without a valid AHCA license or required RN violates Chapter 400 F.S. Skilled service agencies must have 24/7 RN availability. Fines up to $1,000 per day.',
      unknown: 'Verify your license status at ahca.myflorida.com or call AHCA at (850) 412-4403.',
    },
    stateNotes: [
      'Level 2 background screening required',
      'RN available 24/7 for skilled agencies',
      'EVV 85% compliance for Medicaid',
      'License renewal 60 days before expiration',
      'Surety bond required for Medicaid providers',
    ],
  },

  {
    id: 'FL-A2',
    itemId: 'A2',
    stateCode: 'FL',
    title: 'Medicare Certification',
    description: 'Medicare certification for home health agencies in Florida',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Medicare Certification',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com/MCHQ/Home_Care/HHA',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicare-covered home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicaid/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency currently Medicare certified?',
        inputType: 'select',
        options: [
          { value: 'certified', label: 'Yes, fully certified' },
          { value: 'pending', label: 'Certification pending' },
          { value: 'not_certified', label: 'Not Medicare certified' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'When was your last Medicare survey?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: '1_3_years', label: '1-3 years ago' },
          { value: 'over_3_years', label: 'More than 3 years ago' },
          { value: 'never', label: 'Never surveyed' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'certified',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_certified', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicare certification is current or not required for your services.',
      atRisk: 'Medicare certification is pending. Ensure all Conditions of Participation are met.',
      nonCompliant: 'Providing Medicare services without certification violates federal requirements. Contact AHCA at (850) 412-4403.',
      unknown: 'Verify Medicare certification status if providing Medicare-covered services.',
    },
    stateNotes: [
      'AHCA conducts initial certification surveys per 59A-8.003 F.A.C. on behalf of CMS',
      'Must meet 42 CFR 484 Conditions of Participation for Medicare certification',
      'Florida requires state licensure before Medicare certification application per Chapter 400.471 F.S.',
      'Survey deficiencies must be corrected within timeframe specified by AHCA per 59A-8.0135 F.A.C.',
    ],
  },

  {
    id: 'FL-A3',
    itemId: 'A3',
    stateCode: 'FL',
    title: 'Medicaid Provider Enrollment',
    description: 'Florida Medicaid provider enrollment requirements',
    severity: 'high',
    regulationCode: '409.907',
    regulationName: 'Medicaid Enrollment',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(877) 254-1055',
    agencyUrl: 'https://portal.flmmis.com',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide Medicaid-funded services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Medicare/Private Pay only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your agency enrolled as a Florida Medicaid provider?',
        inputType: 'select',
        options: [
          { value: 'enrolled', label: 'Yes, currently enrolled' },
          { value: 'pending', label: 'Enrollment pending' },
          { value: 'not_enrolled', label: 'Not enrolled' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'When is your Medicaid provider revalidation due?',
        inputType: 'date',
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'enrolled',
        },
        helpText: 'Florida requires provider revalidation every 3-5 years',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'enrolled', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'not_enrolled', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medicaid provider enrollment is current per 409.907 F.S.',
      atRisk: 'Medicaid enrollment is pending. Monitor status at portal.flmmis.com.',
      nonCompliant: 'Providing Medicaid services without enrollment violates 409.907 F.S. Contact AHCA Medicaid at (877) 254-1055.',
      unknown: 'Verify Medicaid enrollment status if providing Medicaid-funded services.',
    },
    stateNotes: [
      'Provider revalidation required every 3-5 years per 409.907 F.S.',
      'Surety bond of $50,000 minimum required for Medicaid enrollment per 409.907(7) F.S.',
      'Must disclose all ownership and control interests per 59G-5.020 F.A.C.',
      'Provider enrollment through Florida MMIS portal at portal.flmmis.com',
      'Medicaid fraud is a third-degree felony per 409.920 F.S.',
    ],
  },

  {
    id: 'FL-A4',
    itemId: 'A4',
    stateCode: 'FL',
    title: 'Administrator Qualifications',
    description: 'Florida requires qualified administrator for home health agencies',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Administrator',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated administrator?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What are the administrator\'s qualifications?',
        inputType: 'checklist',
        options: [
          { value: 'degree', label: 'Bachelor\'s degree in health-related field or equivalent experience' },
          { value: 'experience', label: 'Minimum 1 year supervisory experience in home health or related field' },
          { value: 'training', label: 'Completed administrator training/orientation' },
          { value: 'background', label: 'Level 2 background screening cleared' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets Florida qualifications per 59A-8.0095 F.A.C.',
      atRisk: 'Administrator qualifications may be incomplete. Verify all requirements are met.',
      nonCompliant: 'No qualified administrator in place. This violates 59A-8.0095 F.A.C. requirements.',
      unknown: 'Verify administrator qualifications meet 59A-8.0095 F.A.C. requirements.',
    },
    stateNotes: [
      'Administrator must have Level 2 background screening per Chapter 435 F.S.',
      'Administrator responsible for overall agency operation per 59A-8.0095(2) F.A.C.',
      'Must ensure compliance with all applicable state and federal laws per 400.471 F.S.',
      'Changes in administrator must be reported to AHCA within 21 days per 59A-8.003(4) F.A.C.',
    ],
  },

  {
    id: 'FL-A5',
    itemId: 'A5',
    stateCode: 'FL',
    title: 'Director of Nursing Qualifications',
    description: 'Florida requirements for Director of Nursing in skilled agencies',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Director of Nursing',
    agencyName: 'Florida Board of Nursing',
    agencyAbbr: 'FL BON',
    agencyPhone: '(850) 488-0595',
    agencyUrl: 'https://floridasnursing.gov',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency provide skilled nursing services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - Non-skilled services only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you have a designated Director of Nursing (DON)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'What are the DON\'s qualifications?',
        inputType: 'checklist',
        options: [
          { value: 'rn', label: 'Current Florida RN license' },
          { value: 'experience', label: 'Minimum 2 years nursing experience' },
          { value: 'hh_experience', label: 'Home health experience' },
          { value: 'background', label: 'Level 2 background screening cleared' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q3', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Director of Nursing meets Florida requirements or not required for your services.',
      atRisk: 'DON qualifications may be incomplete. Verify Florida RN license and experience requirements.',
      nonCompliant: 'Skilled nursing agency requires qualified DON per 59A-8.0095 F.A.C. Contact Florida BON at (850) 488-0595.',
      unknown: 'If providing skilled services, verify DON qualifications.',
    },
    stateNotes: [
      'DON must hold current Florida RN license per 59A-8.0095(3) F.A.C.',
      'RN must be available 24/7 for skilled service agencies per 59A-8.0095(3)(a) F.A.C.',
      'Level 2 background screening required per Chapter 435 F.S.',
      'Verify RN license at floridasnursing.gov before employment',
      'DON supervises all nursing services and home health aides per 59A-8.0095 F.A.C.',
    ],
  },

  {
    id: 'FL-A6',
    itemId: 'A6',
    stateCode: 'FL',
    title: 'Business Registration',
    description: 'Florida business registration and corporate requirements',
    severity: 'medium',
    regulationCode: '607',
    regulationName: 'Business Registration',
    agencyName: 'Florida Division of Corporations',
    agencyAbbr: 'DOS',
    agencyPhone: '(850) 245-6052',
    agencyUrl: 'https://dos.myflorida.com/sunbiz',
    questions: [
      {
        id: 'q1',
        question: 'Is your business registered with the Florida Division of Corporations?',
        inputType: 'select',
        options: [
          { value: 'active', label: 'Yes, status is Active' },
          { value: 'inactive', label: 'Status is Inactive/Administrative dissolution' },
          { value: 'not_registered', label: 'Not registered' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When is your annual report due?',
        inputType: 'date',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'active',
        },
        helpText: 'Florida requires annual report by May 1st each year',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'active', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'inactive', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'not_registered', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Business registration is active with Florida Division of Corporations.',
      atRisk: 'Business status is inactive. File annual report at sunbiz.org to reinstate.',
      nonCompliant: 'Business is not registered with Florida. Register at sunbiz.org or call (850) 245-6052.',
      unknown: 'Verify business registration at sunbiz.org.',
    },
    stateNotes: [
      'Annual report due by May 1st each year per Chapter 607 F.S.',
      '$400 late fee applies after May 1st per Chapter 607.1622 F.S.',
      'Administrative dissolution occurs for failure to file annual report per 607.1420 F.S.',
      'Business name must match AHCA license per 59A-8.003 F.A.C.',
    ],
  },

  {
    id: 'FL-A7',
    itemId: 'A7',
    stateCode: 'FL',
    title: 'Office Location Requirements',
    description: 'Florida requires home health agencies maintain a primary office location',
    severity: 'medium',
    regulationCode: '59A-8.003',
    regulationName: 'Office Requirements',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain a primary office location in Florida as listed on your license?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is your office accessible during normal business hours?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Are patient records maintained at or accessible from the licensed location?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT rules first (highest priority)
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Then COMPLIANT
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // Then AT_RISK
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Office location meets Florida requirements per 59A-8.003 F.A.C.',
      atRisk: 'Office accessibility or record access may not meet requirements. Verify compliance.',
      nonCompliant: 'No primary office at licensed location violates 59A-8.003 F.A.C. Update license if location changed.',
      unknown: 'Verify office location matches AHCA license.',
    },
    stateNotes: [
      'Primary office address must be on file with AHCA per 59A-8.003 F.A.C.',
      'Address changes must be reported to AHCA within 21 days per 59A-8.003(4) F.A.C.',
      'Patient records must be accessible at licensed location per 59A-8.0095 F.A.C.',
      'Branch office locations require notification to AHCA per 59A-8.003 F.A.C.',
    ],
  },

  {
    id: 'FL-A8',
    itemId: 'A8',
    stateCode: 'FL',
    title: 'Emergency Preparedness Plan',
    description: 'Florida requires comprehensive emergency preparedness for home health agencies',
    severity: 'high',
    regulationCode: '400.492',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written emergency preparedness plan?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your plan address the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hurricane', label: 'Hurricane/severe weather procedures' },
          { value: 'evacuation', label: 'Patient evacuation coordination' },
          { value: 'communication', label: 'Staff and patient communication plan' },
          { value: 'priority', label: 'Priority patient identification system' },
          { value: 'backup', label: 'Backup power/medical equipment plans' },
          { value: 'coordination', label: 'County emergency management coordination' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Florida requires hurricane preparedness given state climate risks',
      },
      {
        id: 'q3',
        question: 'When was your emergency plan last updated?',
        inputType: 'select',
        options: [
          { value: 'current', label: 'Within the past year' },
          { value: 'outdated', label: '1-2 years ago' },
          { value: 'old', label: 'More than 2 years ago' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q4',
        question: 'Has your emergency plan been reviewed/approved by the county health department?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 400.492 F.S., plans must be submitted to county health department for approval',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: No plan or very outdated
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'old', result: 'NON_COMPLIANT' },
      // COMPLIANT: Comprehensive, current, and county-approved
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
        { condition: 'equals', questionId: 'q3', value: 'current' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: Has plan but incomplete, slightly outdated, or not county-approved
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'outdated', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q4', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness plan meets Florida requirements per 400.492 F.S.',
      atRisk: 'Emergency plan may be outdated or incomplete. Update plan before hurricane season and submit to county health department.',
      nonCompliant: 'No emergency preparedness plan. Florida requires written emergency plans per 400.492 F.S. Failure to submit can result in $5,000 fine.',
      unknown: 'Verify emergency preparedness plan exists and is current.',
    },
    stateNotes: [
      'Written emergency plan required per 400.492 F.S. and 59A-8.0095 F.A.C.',
      'Plan must be submitted to county health department for approval per 400.492(2) F.S.',
      'Annual updates required and must address hurricane preparedness per 400.492 F.S.',
      'Priority patient list must be maintained and updated per 400.492(3) F.S.',
      'Failure to submit plan is $5,000 fine per 400.492(5) F.S.',
    ],
  },

  {
    id: 'FL-A11',
    itemId: 'A11',
    stateCode: 'FL',
    title: 'Surety Bond Requirement (Conditional)',
    description: 'Florida surety bond requirements vary by agency type and ownership',
    severity: 'high',
    regulationCode: '408.8065',
    regulationName: 'Surety Bond',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following applies to your agency? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'medicaid', label: 'Enrolled as Florida Medicaid provider' },
          { value: 'nonimmigrant', label: 'Owned/controlled by nonimmigrant alien(s)' },
          { value: 'sanctions', label: 'Had license sanctions or terminations in past 5 years' },
          { value: 'none', label: 'None of the above' },
        ],
        required: true,
        helpText: 'Surety bond requirements depend on these factors',
      },
      {
        id: 'q2',
        question: 'Do you maintain the required surety bond?',
        inputType: 'select',
        options: [
          { value: 'yes_current', label: 'Yes, bond is current' },
          { value: 'yes_expiring', label: 'Yes, but expiring within 30 days' },
          { value: 'no', label: 'No surety bond' },
          { value: 'na', label: 'N/A - no bond requirement applies' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notIncludes',
          value: 'none',
        },
      },
      {
        id: 'q3',
        question: 'What is your surety bond amount?',
        inputType: 'select',
        options: [
          { value: '500k', label: '$500,000 (nonimmigrant alien/sanctions requirement)' },
          { value: '50k_plus', label: '$50,000 or more (Medicaid provider requirement)' },
          { value: 'below', label: 'Less than $50,000' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'notEquals',
          value: 'na',
        },
        helpText: 'Medicaid providers: $50,000 minimum per 409.907 F.S. Nonimmigrant alien owners: $500,000 per 408.8065 F.S.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['none'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes_current' },
        { condition: 'includes', questionId: 'q3', value: ['500k', '50k_plus'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes_expiring' },
        { condition: 'equals', questionId: 'q3', value: 'below' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Surety bond meets Florida requirements or not required for your agency type.',
      atRisk: 'Surety bond may be expiring or below required amount. Medicaid: $50,000 minimum. Nonimmigrant alien owners: $500,000.',
      nonCompliant: 'Operating without required surety bond. Medicaid providers must have bond per 409.907 F.S. Nonimmigrant alien owners require $500,000 bond per 408.8065 F.S.',
      unknown: 'Verify if surety bond requirements apply to your agency.',
    },
    stateNotes: [
      'Medicaid providers require $50,000 surety bond per 409.907(7) F.S.',
      'Nonimmigrant alien owners require $500,000 bond per 408.8065 F.S.',
      'Prior sanctions/terminations may require additional bond per 408.8065(2) F.S.',
      'Bond must name AHCA as beneficiary and remain in effect during licensure',
      'Bond must be from company authorized to do business in Florida',
    ],
  },

  {
    id: 'FL-A12',
    itemId: 'A12',
    stateCode: 'FL',
    title: 'Insurance Requirements',
    description: 'Florida requires specific insurance coverage for home health agencies',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Insurance',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'gl_1m', label: 'General liability - $1,000,000 minimum' },
          { value: 'prof', label: 'Professional liability insurance' },
          { value: 'wc', label: 'Workers\' compensation insurance' },
          { value: 'auto', label: 'Commercial auto insurance (if applicable)' },
        ],
        required: true,
        helpText: 'Florida law requires minimum coverage for licensed home health agencies',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Insurance coverage meets Florida requirements.',
      atRisk: 'Insurance coverage may not meet all Florida requirements. Verify minimum $1,000,000 general liability.',
      nonCompliant: 'Insurance does not meet Florida requirements. Ensure minimum $1,000,000 liability coverage.',
      unknown: 'Verify insurance meets Florida requirements.',
    },
    stateNotes: [
      'General liability insurance of $1,000,000 minimum recommended per 59A-8.0095 F.A.C.',
      'Professional liability (malpractice) insurance required for skilled services',
      'Workers compensation required per Chapter 440 F.S. if 4+ employees',
      'Proof of insurance may be requested during AHCA survey per 59A-8.0135 F.A.C.',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'FL-B1',
    itemId: 'B1',
    stateCode: 'FL',
    title: 'Personnel File Requirements',
    description: 'Florida requires specific documents in each employee\'s personnel file',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Personnel Records',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following documents do you maintain in EVERY employee\'s personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'app', label: 'Employment application' },
          { value: 'id', label: 'Government-issued photo ID (Florida DL, passport, or state ID)' },
          { value: 'level2', label: 'Level 2 background screening clearance' },
          { value: 'abuse', label: 'Abuse registry clearance' },
          { value: 'i9', label: 'I-9 Employment Eligibility Verification' },
          { value: 'job_desc', label: 'Signed job description' },
          { value: 'training', label: 'Training documentation (40 hours for state-licensed / 75 hours for Medicare-Medicaid)' },
          { value: 'competency', label: 'Competency evaluation' },
          { value: 'tb', label: 'TB screening results' },
          { value: 'cpr', label: 'Current CPR certification' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all Florida-required documentation per 59A-8.0095 F.A.C.',
      atRisk: 'Some personnel files are missing required documents. AHCA surveyors will review files for completeness. Missing: {missing}.',
      nonCompliant: 'Personnel files are missing critical documentation required by 59A-8.0095 F.A.C. Immediately obtain: {missing}.',
      unknown: 'Audit personnel files against 59A-8.0095 F.A.C. requirements.',
    },
    stateNotes: [
      'Level 2 background screening required for all employees per Chapter 435 F.S.',
      'Training documentation required: 40 hours state / 75 hours Medicare-Medicaid per 59A-8.0095(5) F.A.C.',
      'TB screening must be completed before patient contact per 59A-8.0095 F.A.C.',
      'Personnel files must be maintained at licensed location per 59A-8.0095 F.A.C.',
      'Records must be retained for 5 years after employment ends per 59A-8.0095 F.A.C.',
    ],
  },

  {
    id: 'FL-B2',
    itemId: 'B2',
    stateCode: 'FL',
    title: 'Professional License Verification',
    description: 'Florida requires verification of professional licenses for clinical staff',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'License Verification',
    agencyName: 'Florida Board of Nursing',
    agencyAbbr: 'FL BON',
    agencyPhone: '(850) 488-0595',
    agencyUrl: 'https://mqa-internet.doh.state.fl.us/MQASearchServices/HealthCareProviders',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses before clinical staff begin work?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified before first day' },
          { value: 'sometimes', label: 'Sometimes delayed verification' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which licenses do you verify? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'rn', label: 'RN licenses (Florida BON)' },
          { value: 'lpn', label: 'LPN licenses (Florida BON)' },
          { value: 'cna', label: 'CNA certifications (Florida BON)' },
          { value: 'pt', label: 'Physical therapy licenses' },
          { value: 'ot', label: 'Occupational therapy licenses' },
          { value: 'sw', label: 'Social work licenses' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you check for disciplinary actions on licenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Florida BON online lookup shows license status and any disciplinary actions',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'sometimes' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // Not verifying all required license types per 59A-8.0095 F.A.C.
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets Florida requirements.',
      atRisk: 'License verification process may be incomplete. Verify all licenses before first day, check for disciplinary actions, and ensure all license types are covered.',
      nonCompliant: 'No license verification process or not verifying required license types. This violates 59A-8.0095 F.A.C. Contact Florida BON at (850) 488-0595.',
      unknown: 'Verify license verification process meets 59A-8.0095 F.A.C. requirements.',
    },
    stateNotes: [
      'Verify all professional licenses before first day of employment per 59A-8.0095 F.A.C.',
      'Use Florida DOH license lookup at mqa-internet.doh.state.fl.us',
      'Check for disciplinary actions and license restrictions per 456.072 F.S.',
      'RN/LPN licenses must be valid Florida licenses per Chapter 464 F.S.',
      'Document license verification in personnel file per 59A-8.0095 F.A.C.',
    ],
  },

  {
    id: 'FL-B3',
    itemId: 'B3',
    stateCode: 'FL',
    title: 'Employee Health Documentation',
    description: 'Florida requires health screening documentation for direct care employees',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Employee Health',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Which health documents do you maintain for direct care staff? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'tb', label: 'TB screening (2-step or IGRA blood test)' },
          { value: 'hepb', label: 'Hepatitis B vaccination status/declination' },
          { value: 'flu', label: 'Annual influenza vaccination status/declination' },
          { value: 'covid', label: 'COVID-19 vaccination status (if required by facility contracts)' },
          { value: 'physical', label: 'Pre-employment physical (if required)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is TB screening completed before employees provide direct client care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before client contact' },
          { value: 'no', label: 'No, sometimes after start date' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Employee health documentation meets Florida requirements per 59A-8.0095 F.A.C.',
      atRisk: 'Health documentation may be incomplete. Ensure TB screening before client contact.',
      nonCompliant: 'Employee health documentation is insufficient. 59A-8.0095 F.A.C. requires TB screening at minimum.',
      unknown: 'Verify health documentation requirements per 59A-8.0095 F.A.C.',
    },
    stateNotes: [
      'TB screening required before patient contact per 59A-8.0095 F.A.C.',
      'Two-step TB test or IGRA blood test acceptable per Florida DOH guidelines',
      'Hepatitis B vaccination or declination required per OSHA 29 CFR 1910.1030',
      'Annual TB risk assessment recommended per CDC guidelines',
      'Maintain health records in personnel file per 59A-8.0095 F.A.C.',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'FL-C1',
    itemId: 'C1',
    stateCode: 'FL',
    title: 'Level 2 Background Screening (Chapter 435 F.S.)',
    description: 'Florida requires Level 2 background screening for all direct care staff',
    severity: 'high',
    regulationCode: '435',
    regulationName: 'Background Screening',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com/MCHQ/Central_Services/Background_Screening',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees complete Level 2 background screening through the AHCA Clearinghouse?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have Level 2 screening via Clearinghouse' },
          { value: 'most', label: 'Most have it, some pending' },
          { value: 'level1', label: 'Using Level 1 screening only' },
          { value: 'no', label: 'No background screening' },
        ],
        required: true,
        helpText: 'Chapter 435 F.S. requires Level 2 (fingerprint-based) screening for home health employees. Screenings must be initiated through the AHCA Clearinghouse.',
      },
      {
        id: 'q2',
        question: 'Are background screenings completed BEFORE employees have unsupervised client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before first unsupervised contact' },
          { value: 'no', label: 'No, sometimes employees start before clearance' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you check the Florida Abuse Registry for all employees?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before background check clearance (direct violation)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'includes', questionId: 'q1', value: ['level1', 'no'], result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Level 2 background screening is complete for all employees per Chapter 435 F.S.',
      atRisk: 'Some employees may be pending Level 2 screening. Ensure screening is completed before unsupervised client contact.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance OR working without required Level 2 screening. Per Chapter 435 F.S., NO unsupervised client contact is allowed until Level 2 screening is completed. Level 1 screening is NOT sufficient. Contact AHCA Background Screening at (850) 412-4403.',
      unknown: 'Verify all employees have Level 2 background screening per Chapter 435 F.S.',
    },
    stateNotes: [
      'Level 2 (fingerprint-based) screening required per Chapter 435 F.S.',
      'Screening must be initiated through AHCA Clearinghouse per 435.12 F.S.',
      'No unsupervised patient contact until screening is complete per 435.06 F.S.',
      'Abuse registry check also required per 435.04 F.S.',
      'Level 1 (name-based) screening is NOT sufficient for home health per 435.03 F.S.',
    ],
  },

  {
    id: 'FL-C4',
    itemId: 'C4',
    stateCode: 'FL',
    title: 'Background Screening Renewal (435.12 F.S.)',
    description: 'Florida requires background screening renewal every 5 years',
    severity: 'high',
    regulationCode: '435.12',
    regulationName: 'Screening Retention',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you renew employee background screenings every 5 years as required by Florida?',
        inputType: 'select',
        options: [
          { value: 'yes_tracked', label: 'Yes, with automated expiration tracking via Clearinghouse' },
          { value: 'yes_manual', label: 'Yes, tracked manually' },
          { value: 'no', label: 'No renewal process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Have you enrolled employees in the AHCA Clearinghouse fingerprint retention program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'AHCA Clearinghouse provides ongoing arrest notifications and 5-year retention',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['yes_tracked', 'yes_manual'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes_manual' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background screening renewal process is in place with Clearinghouse enrollment per 435.12 F.S.',
      atRisk: 'Consider enrolling in AHCA Clearinghouse fingerprint retention for continuous monitoring. Manual tracking increases risk of expired screenings.',
      nonCompliant: 'No background screening renewal process exists. Florida requires renewal every 5 years per 435.12 F.S.',
      unknown: 'Review background screening renewal requirements under 435.12 F.S.',
    },
    stateNotes: [
      'Background screening expires after 5 years per 435.12 F.S.',
      'AHCA Clearinghouse retention program provides ongoing arrest notifications per 435.12(2)(c) F.S.',
      'Fingerprint retention fee applies but eliminates need for rescreening per 435.12 F.S.',
      'Expired screenings result in employee being ineligible for patient contact',
      'Track expiration dates proactively to avoid compliance gaps',
    ],
  },

  {
    id: 'FL-C2',
    itemId: 'C2',
    stateCode: 'FL',
    title: 'FDLE Fingerprint Submission',
    description: 'Florida requires fingerprint submission through FDLE for Level 2 screening',
    severity: 'high',
    regulationCode: '435',
    regulationName: 'FDLE Fingerprints',
    agencyName: 'Florida Department of Law Enforcement',
    agencyAbbr: 'FDLE',
    agencyPhone: '(850) 410-7100',
    agencyUrl: 'https://www.fdle.state.fl.us/Background-Checks',
    questions: [
      {
        id: 'q1',
        question: 'Where do employees submit fingerprints for Level 2 screening?',
        inputType: 'select',
        options: [
          { value: 'livescan', label: 'AHCA-approved Livescan vendor (Clearinghouse compliant)' },
          { value: 'fdle', label: 'FDLE regional office' },
          { value: 'other', label: 'Other method' },
          { value: 'unsure', label: 'Not sure' },
        ],
        required: true,
        helpText: 'Florida uses electronic Livescan for fingerprint submission to FDLE. Must be Clearinghouse compliant.',
      },
      {
        id: 'q2',
        question: 'Are fingerprints submitted with a photograph as required?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 435.12 F.S., fingerprints must be submitted with a photograph taken at time of submission',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'livescan' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'fdle', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'other', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'unsure', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Fingerprint submission process uses FDLE-approved methods.',
      atRisk: 'Verify fingerprints are submitted through AHCA-approved Clearinghouse-compliant Livescan vendor.',
      nonCompliant: 'Fingerprint process may not meet Chapter 435 F.S. requirements. Contact FDLE at (850) 410-7100.',
      unknown: 'Verify fingerprint submission process with FDLE.',
    },
    stateNotes: [
      'Fingerprints must be submitted through AHCA-approved Livescan vendor per 435.12 F.S.',
      'Photograph required at time of fingerprint submission per 435.12(2)(a) F.S.',
      'Electronic submission (Livescan) required - hard cards not accepted per FDLE requirements',
      'Use only Clearinghouse-compliant vendors listed on AHCA website',
      'Results typically available within 3-5 business days through Clearinghouse',
    ],
  },

  {
    id: 'FL-C3',
    itemId: 'C3',
    stateCode: 'FL',
    title: 'Abuse Registry Check',
    description: 'Florida requires abuse registry verification for all direct care staff',
    severity: 'high',
    regulationCode: '435.04',
    regulationName: 'Abuse Registry',
    agencyName: 'Department of Children and Families',
    agencyAbbr: 'DCF',
    agencyPhone: '(850) 487-1111',
    agencyUrl: 'https://www.myflfamilies.com/services/abuse',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the Florida Abuse Registry (DCF) for all new employees?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, for all employees before hire' },
          { value: 'some', label: 'Sometimes or only for certain roles' },
          { value: 'no', label: 'No abuse registry checks' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is abuse registry clearance obtained BEFORE unsupervised client contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse registry screening meets Florida requirements per 435.04 F.S.',
      atRisk: 'Abuse registry checks may be inconsistent. Verify all employees before client contact.',
      nonCompliant: 'Abuse registry checks are not being performed. This violates 435.04 F.S. Contact DCF at (850) 487-1111.',
      unknown: 'Verify abuse registry screening process.',
    },
    stateNotes: [
      'Abuse registry check required before patient contact per 435.04 F.S.',
      'Florida Abuse Hotline maintained by DCF at (800) 962-2873',
      'Confirmed abuse findings disqualify employee from employment per 435.04 F.S.',
      'Registry check is separate from Level 2 background screening',
      'Document abuse registry clearance in personnel file per 59A-8.0095 F.A.C.',
    ],
  },

  {
    id: 'FL-C5',
    itemId: 'C5',
    stateCode: 'FL',
    title: 'Disqualifying Offenses',
    description: 'Florida specifies disqualifying offenses for home health employment',
    severity: 'high',
    regulationCode: '435.04',
    regulationName: 'Disqualifying Offenses',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com/MCHQ/Central_Services/Background_Screening',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy for handling disqualifying offenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you know which offenses are disqualifying under Chapter 435 F.S.?',
        inputType: 'select',
        options: [
          { value: 'yes_list', label: 'Yes, we maintain a reference list' },
          { value: 'yes_general', label: 'Yes, general awareness' },
          { value: 'no', label: 'No / Not familiar' },
        ],
        helpText: 'Disqualifying offenses include abuse, neglect, exploitation, domestic violence, and various felonies per 435.04 F.S.',
      },
      {
        id: 'q3',
        question: 'Are you aware of the exemption process for employees with disqualifying offenses?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, familiar with exemption process' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: no policy OR not familiar with disqualifying offenses
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // COMPLIANT: has policy AND maintains reference list
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes_list' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: has policy but only general awareness OR not familiar with exemption process
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes_general' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Disqualifying offense policy and knowledge is in place per 435.04 F.S.',
      atRisk: 'Has policy but needs to review disqualifying offense list or exemption process. Training recommended.',
      nonCompliant: 'No policy for handling disqualifying offenses OR not familiar with Chapter 435 F.S. requirements. Implement policy immediately.',
      unknown: 'Review disqualifying offense requirements under 435.04 F.S.',
    },
    stateNotes: [
      'Disqualifying offenses listed in 435.04 F.S. include abuse, neglect, exploitation, and violent felonies',
      'Exemption process available through AHCA per 435.07 F.S.',
      'Exemption requires 3 years since completion of sentence for most offenses per 435.07 F.S.',
      'Some offenses (murder, sexual battery) have no exemption available per 435.07(4) F.S.',
      'Document all background check results and any exemptions granted',
    ],
  },

  {
    id: 'FL-C6',
    itemId: 'C6',
    stateCode: 'FL',
    title: 'OIG Exclusion List Check',
    description: 'Federal requirement to check OIG exclusion list for healthcare employees',
    severity: 'high',
    regulationCode: '42 U.S.C. 1320a-7',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG List of Excluded Individuals/Entities (LEIE) for all employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, monthly screening' },
          { value: 'hire', label: 'Only at time of hire' },
          { value: 'no', label: 'No OIG checks' },
        ],
        required: true,
        helpText: 'OIG recommends monthly checks. Federal law prohibits employing excluded individuals for federally-funded services.',
      },
      {
        id: 'q2',
        question: 'Do you also check the SAM.gov exclusion list?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'monthly' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'hire' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion screening is performed monthly per federal requirements.',
      atRisk: 'OIG checks at hire only is insufficient. Implement monthly screening of LEIE and SAM.gov.',
      nonCompliant: 'No OIG exclusion screening. This violates federal law and jeopardizes Medicare/Medicaid billing.',
      unknown: 'Verify OIG exclusion screening process.',
    },
    stateNotes: [
      'OIG LEIE check required for Medicare/Medicaid providers per 42 U.S.C. 1320a-7',
      'Monthly screening recommended by OIG Special Advisory Bulletin',
      'SAM.gov exclusion check also required for federal healthcare programs',
      'Employing excluded individual can result in Civil Monetary Penalties up to $10,000/day',
      'Florida Medicaid may terminate provider agreement for employing excluded individuals',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'FL-D1',
    itemId: 'D1',
    stateCode: 'FL',
    title: 'TB Screening',
    description: 'Florida requires TB screening for all direct care employees',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Employee Health',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care employees have TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees have TB screening' },
          { value: 'most', label: 'Most do, some missing' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Is TB screening completed BEFORE employees provide direct client care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, always before client contact' },
          { value: 'no', label: 'No, sometimes after they start' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first: not tracking TB screening at all
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // COMPLIANT: all employees have TB screening AND done before client contact
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: most have screening OR screening not always done before client contact
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening is current for all direct care employees per 59A-8.0095 F.A.C.',
      atRisk: 'Some TB screenings may be missing or employees started before screening. Florida requires TB screening before direct client contact.',
      nonCompliant: 'Direct care employees are working without TB screening. This violates 59A-8.0095 F.A.C. Obtain TB screening for all employees immediately.',
      unknown: 'Verify TB screening status for all direct care employees.',
    },
    stateNotes: [
      'TB screening required before patient contact per 59A-8.0095 F.A.C.',
      'Two-step TST or single IGRA blood test acceptable per Florida DOH guidelines',
      'Positive TB test requires chest X-ray and physician clearance before patient contact',
      'Annual symptom screening recommended for employees with prior positive TB test',
      'Document TB status and any follow-up testing in personnel file',
    ],
  },

  {
    id: 'FL-D2',
    itemId: 'D2',
    stateCode: 'FL',
    title: 'Infection Control Program',
    description: 'Florida requires home health agencies to have infection control policies',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Infection Control',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written infection control policy?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which infection control measures are included in your policy? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'handwashing', label: 'Hand hygiene procedures' },
          { value: 'ppe', label: 'PPE usage requirements' },
          { value: 'isolation', label: 'Isolation/transmission precautions' },
          { value: 'sharps', label: 'Sharps and biohazard disposal' },
          { value: 'outbreak', label: 'Outbreak response procedures' },
          { value: 'training', label: 'Staff infection control training' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program is comprehensive per 59A-8.0095 F.A.C.',
      atRisk: 'Infection control policy may be incomplete. Review and expand to include all required elements.',
      nonCompliant: 'No infection control policy or policy is inadequate. This violates 59A-8.0095 F.A.C.',
      unknown: 'Review infection control requirements per 59A-8.0095 F.A.C.',
    },
    stateNotes: [
      'Written infection control policy required per 59A-8.0095 F.A.C.',
      'Staff training on infection control required annually per 59A-8.0095 F.A.C.',
      'PPE must be available and staff trained on proper use per OSHA 29 CFR 1910.1030',
      'Sharps disposal must comply with Florida DEP regulations per 381.0098 F.S.',
      'Report communicable disease outbreaks to Florida DOH per 381.0031 F.S.',
    ],
  },

  {
    id: 'FL-D3',
    itemId: 'D3',
    stateCode: 'FL',
    title: 'CPR/First Aid Certification',
    description: 'CPR and First Aid certification requirements for direct care staff',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Emergency Training',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL direct care staff have current CPR certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have current CPR' },
          { value: 'most', label: 'Most have current CPR' },
          { value: 'no', label: 'CPR not required or tracked' },
        ],
        required: true,
        helpText: 'Per 59A-8.0095 F.A.C., all home health aides must maintain current CPR certification',
      },
      {
        id: 'q2',
        question: 'Do staff have First Aid certification?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have current First Aid' },
          { value: 'most', label: 'Most have current First Aid' },
          { value: 'no', label: 'First Aid not required or tracked' },
        ],
      },
      {
        id: 'q3',
        question: 'What CPR certification do you accept?',
        inputType: 'checklist',
        options: [
          { value: 'aha', label: 'American Heart Association (AHA)' },
          { value: 'arc', label: 'American Red Cross' },
          { value: 'hsi', label: 'Health & Safety Institute (HSI)' },
          { value: 'other', label: 'Other accredited provider' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CPR and First Aid certification is current for all direct care staff.',
      atRisk: 'Some staff may have expired or missing CPR/First Aid certifications. Update immediately.',
      nonCompliant: 'CPR certification is not required or tracked. 59A-8.0095 F.A.C. requires CPR certification for all home health aides.',
      unknown: 'Verify CPR/First Aid certification status for all direct care staff.',
    },
    stateNotes: [
      'CPR certification required for all home health aides per 59A-8.0095(5)(d)14 F.A.C.',
      'Certification must be from AHA, Red Cross, or equivalent accredited provider',
      'CPR certification typically expires every 2 years - track renewal dates',
      'First Aid certification recommended but verify specific payer requirements',
      'Document CPR/First Aid certifications in personnel file per 59A-8.0095 F.A.C.',
    ],
  },

  {
    id: 'FL-D4',
    itemId: 'D4',
    stateCode: 'FL',
    title: 'Incident Reporting',
    description: 'Florida requires reporting of adverse incidents to AHCA',
    severity: 'high',
    regulationCode: '400.474',
    regulationName: 'Incident Reporting',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(888) 419-3456',
    agencyUrl: 'https://ahca.myflorida.com/MCHQ/Health_Facility_Regulation',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an incident reporting policy?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are you aware of which incidents must be reported to AHCA?',
        inputType: 'select',
        options: [
          { value: 'yes_list', label: 'Yes, we have a reference list' },
          { value: 'yes_general', label: 'Yes, general awareness' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Reportable incidents include abuse, neglect, exploitation, and serious injuries per 400.474 F.S.',
      },
      {
        id: 'q3',
        question: 'How do you report incidents to AHCA?',
        inputType: 'select',
        options: [
          { value: 'online', label: 'AHCA online reporting system' },
          { value: 'phone', label: 'AHCA hotline' },
          { value: 'both', label: 'Both methods available' },
          { value: 'unsure', label: 'Not sure how to report' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT first - no policy or no awareness
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // AT_RISK - general awareness only OR unsure how to report
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'yes_general' },
        { condition: 'equals', questionId: 'q3', value: 'unsure' },
      ], result: 'AT_RISK' },
      // COMPLIANT - policy + reference list + knows how to report
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes_list' },
        { condition: 'includes', questionId: 'q3', value: ['online', 'phone', 'both'] },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting policy and procedures meet Florida requirements.',
      atRisk: 'Incident reporting knowledge may be incomplete. Review AHCA reporting requirements.',
      nonCompliant: 'No incident reporting policy or unaware of requirements. This violates 400.474 F.S. Contact AHCA at (888) 419-3456.',
      unknown: 'Review incident reporting requirements under 400.474 F.S.',
    },
    stateNotes: [
      'Adverse incidents must be reported to AHCA within 1 business day per 400.474 F.S.',
      'Reportable incidents include abuse, neglect, exploitation, and serious injuries per 400.474 F.S.',
      'Use AHCA online incident reporting system at ahca.myflorida.com',
      'Internal incident documentation must be maintained per 59A-8.0095 F.A.C.',
      'Failure to report can result in license action per 400.474(5) F.S.',
    ],
  },

  {
    id: 'FL-D5',
    itemId: 'D5',
    stateCode: 'FL',
    title: 'Abuse and Neglect Prevention',
    description: 'Florida requires abuse and neglect prevention policies and reporting',
    severity: 'high',
    regulationCode: '415.1034',
    regulationName: 'APS Reporting',
    agencyName: 'Department of Children and Families',
    agencyAbbr: 'DCF',
    agencyPhone: '(800) 962-2873',
    agencyUrl: 'https://www.myflfamilies.com/services/abuse',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written policy on abuse, neglect, and exploitation prevention?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are all staff trained on mandatory reporting requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff trained' },
          { value: 'some', label: 'Some staff trained' },
          { value: 'no', label: 'No training on mandatory reporting' },
        ],
        helpText: 'Florida law requires immediate reporting of suspected abuse to DCF Adult Protective Services',
      },
      {
        id: 'q3',
        question: 'Do staff know the Florida Abuse Hotline number?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, posted and communicated' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Florida Abuse Hotline: 1-800-962-2873',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'some' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Abuse/neglect prevention policy and training meets Florida requirements per 415.1034 F.S.',
      atRisk: 'Some staff may need training on mandatory reporting. Train all staff and post abuse hotline.',
      nonCompliant: 'No abuse prevention policy or training. This violates 415.1034 F.S. Florida Abuse Hotline: 1-800-962-2873.',
      unknown: 'Review abuse/neglect prevention requirements under 415.1034 F.S.',
    },
    stateNotes: [
      'All staff are mandatory reporters of abuse/neglect per 415.1034 F.S.',
      'Reports must be made immediately to DCF Abuse Hotline: 1-800-962-2873',
      'Failure to report is a second-degree misdemeanor per 415.111 F.S.',
      'Staff must be trained on recognizing signs of abuse and neglect',
      'Post Florida Abuse Hotline number in visible location per 415.1034 F.S.',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'FL-E1',
    itemId: 'E1',
    stateCode: 'FL',
    title: 'Initial Training Requirements',
    description: 'Florida requires 40 hours training for state-licensed agencies; 75 hours for Medicare/Medicaid certified agencies',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Aide Training',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare and/or Medicaid certified?',
        inputType: 'select',
        options: [
          { value: 'medicare_medicaid', label: 'Yes - Medicare and/or Medicaid certified' },
          { value: 'state_only', label: 'No - State licensed only (non-Medicare/Medicaid)' },
        ],
        required: true,
        helpText: 'Training requirements differ based on certification status',
      },
      {
        id: 'q2',
        question: 'Do ALL new home health aides complete the required initial training hours?',
        inputType: 'select',
        options: [
          { value: 'all_75', label: 'Yes, all complete 75+ hours (Medicare/Medicaid requirement)' },
          { value: 'all_40', label: 'Yes, all complete 40+ hours (State requirement)' },
          { value: 'most', label: 'Most complete required hours' },
          { value: 'less', label: 'Training is less than required' },
        ],
        required: true,
        helpText: 'State-licensed only: 40 hours per 59A-8.0095(5)(c) F.A.C. Medicare/Medicaid: 75 hours per 42 CFR 484.36',
      },
      {
        id: 'q3',
        question: 'For Medicare/Medicaid agencies: Does your training include the 16-hour clinical component?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, supervised clinical training included' },
          { value: 'no', label: 'No clinical component' },
          { value: 'na', label: 'N/A - State licensed only' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'medicare_medicaid',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'medicare_medicaid' },
        { condition: 'equals', questionId: 'q2', value: 'all_75' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'state_only' },
        { condition: 'includes', questionId: 'q2', value: ['all_75', 'all_40'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'most' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'less', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Initial training program meets Florida requirements. State-licensed: 40 hours per 59A-8.0095(5)(c). Medicare/Medicaid: 75 hours with 16-hour clinical per 42 CFR 484.36.',
      atRisk: 'Initial training may not fully meet requirements. Verify correct hours for your certification type.',
      nonCompliant: 'Training program does not meet Florida requirements. State-licensed agencies: minimum 40 hours. Medicare/Medicaid agencies: minimum 75 hours with 16-hour clinical component.',
      unknown: 'Review initial training program against applicable requirements.',
    },
    stateNotes: [
      'State-licensed agencies: 40 hours initial training per 59A-8.0095(5)(c) F.A.C.',
      'Medicare/Medicaid agencies: 75 hours with 16-hour clinical per 42 CFR 484.36',
      'Training curriculum must include topics listed in 59A-8.0095(5)(d) F.A.C.',
      'Training may be provided by agency or approved training program',
      'Document all training hours and completion dates in personnel file',
    ],
  },

  {
    id: 'FL-E2',
    itemId: 'E2',
    stateCode: 'FL',
    title: 'Competency Evaluation',
    description: 'Florida requires competency evaluation for home health aides',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Competency',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct competency evaluations for all home health aides?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all aides are evaluated' },
          { value: 'some', label: 'Some aides are evaluated' },
          { value: 'no', label: 'No competency evaluations' },
        ],
        required: true,
        helpText: 'Per 59A-8.0095(5)(i), HHAs may pass competency test in lieu of 40-hour training',
      },
      {
        id: 'q2',
        question: 'When are competency evaluations conducted?',
        inputType: 'checklist',
        options: [
          { value: 'initial', label: 'After initial training' },
          { value: 'annual', label: 'Annually' },
          { value: 'new_skill', label: 'When new skills are assigned' },
          { value: 'performance', label: 'When performance issues arise' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
      {
        id: 'q3',
        question: 'Who conducts competency evaluations?',
        inputType: 'select',
        options: [
          { value: 'rn', label: 'RN conducts/supervises evaluation' },
          { value: 'lpn_rn', label: 'LPN under RN supervision' },
          { value: 'other', label: 'Other staff member' },
          { value: 'unsure', label: 'Not sure' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
        helpText: 'Per 59A-8.0095(5)(i), practical competency test must be administered by RN or LPN under RN supervision',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: no evaluations OR evaluations by unqualified staff
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'other', result: 'NON_COMPLIANT' },
      // COMPLIANT: all aides evaluated + proper timing + RN/LPN conducts
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 2 },
        { condition: 'includes', questionId: 'q3', value: ['rn', 'lpn_rn'] },
      ], result: 'COMPLIANT' },
      // AT_RISK: some aides only, timing issues, or unsure who conducts
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'countLessThan', questionId: 'q2', value: 2 },
        { condition: 'equals', questionId: 'q3', value: 'unsure' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Competency evaluation process meets Florida requirements per 59A-8.0095(5)(i) F.A.C.',
      atRisk: 'Competency evaluations may be inconsistent or conducted by unverified staff. Ensure RN/LPN supervision per 59A-8.0095(5)(i).',
      nonCompliant: 'No competency evaluations OR evaluations by unqualified staff. Per 59A-8.0095(5)(i), must be RN or LPN under RN supervision.',
      unknown: 'Review competency evaluation requirements per 59A-8.0095(5)(i) F.A.C.',
    },
    stateNotes: [
      'Competency test may be passed in lieu of 40-hour training per 59A-8.0095(5)(i) F.A.C.',
      'Test must be administered by RN or LPN under RN supervision per 59A-8.0095(5)(i) F.A.C.',
      'Practical skills evaluation required - not just written test',
      'Re-evaluate competency when new skills are assigned',
      'Document competency evaluation results in personnel file',
    ],
  },

  {
    id: 'FL-E3',
    itemId: 'E3',
    stateCode: 'FL',
    title: 'Annual In-Service Training',
    description: 'Medicare/Medicaid certified agencies require 12 hours of annual in-service training',
    severity: 'high',
    regulationCode: '42 CFR 484.36',
    regulationName: 'Annual Training',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Is your agency Medicare and/or Medicaid certified?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - Medicare/Medicaid certified' },
          { value: 'no', label: 'No - State licensed only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL home health aides complete 12 hours of annual in-service training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours' },
          { value: 'no', label: 'No structured annual training' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: '42 CFR 484.36(b) requires 12 hours annual in-service for Medicare/Medicaid certified agencies',
      },
      {
        id: 'q3',
        question: 'Do you provide ongoing training for state-licensed staff?',
        inputType: 'select',
        options: [
          { value: 'regular', label: 'Yes, regular ongoing training' },
          { value: 'occasional', label: 'Occasional training' },
          { value: 'none', label: 'No ongoing training program' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'no',
        },
        helpText: 'While not mandated at 12 hours for state-only agencies, ongoing training is best practice',
      },
    ],
    scoringRules: [
      // Medicare/Medicaid agencies - require 12 hours annual in-service
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'most' },
      ], result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // State-licensed only agencies - ongoing training recommended
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'regular' },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'occasional' },
      ], result: 'AT_RISK' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'none' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual training program meets requirements. Medicare/Medicaid agencies: 12 hours per 42 CFR 484.36(b).',
      atRisk: 'Some employees may be behind on annual training. Medicare/Medicaid agencies require 12 hours annually.',
      nonCompliant: 'No structured annual training program. Medicare/Medicaid certified agencies MUST provide 12 hours annual in-service per federal CoPs.',
      unknown: 'Review annual training requirements based on your certification status.',
    },
    stateNotes: [
      'Medicare/Medicaid agencies: 12 hours annual in-service per 42 CFR 484.36(b)',
      'State-licensed only agencies: ongoing training recommended per 59A-8.0095 F.A.C.',
      'Track 12-month training period for each employee',
      'Training topics should address identified competency gaps',
      'Document all in-service training hours in personnel file',
    ],
  },

  {
    id: 'FL-E4',
    itemId: 'E4',
    stateCode: 'FL',
    title: 'Alzheimer\'s Disease Training',
    description: 'Florida requires Alzheimer\'s training for staff serving dementia patients',
    severity: 'medium',
    regulationCode: '400.4785',
    regulationName: 'Dementia Training',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do any of your clients have Alzheimer\'s disease or related dementias?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Have staff serving these clients completed Florida\'s required Alzheimer\'s/dementia training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff serving dementia clients (minimum 2 hours)' },
          { value: 'some', label: 'Some staff have completed it' },
          { value: 'no', label: 'No Alzheimer\'s training provided' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 430.5025 F.S., staff providing services to persons with ADRD must receive specific training',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Alzheimer\'s training requirements are met per 400.4785 F.S. / 430.5025 F.S.',
      atRisk: 'Not all staff serving dementia clients have completed required Alzheimer\'s training. Complete training for all applicable staff.',
      nonCompliant: 'Staff are serving dementia clients without required Alzheimer\'s training. This violates 430.5025 F.S.',
      unknown: 'If serving dementia clients, verify Alzheimer\'s training requirements.',
    },
    stateNotes: [
      'Minimum 2 hours Alzheimer\'s/dementia training required per 430.5025 F.S.',
      'Training must cover communication, behavior management, and safety',
      'Applies to all staff providing direct care to ADRD patients',
      'Training must be completed before serving dementia patients',
      'Florida offers approved ADRD training curricula through Elder Affairs',
    ],
  },

  {
    id: 'FL-E5',
    itemId: 'E5',
    stateCode: 'FL',
    title: 'HIV/AIDS Training',
    description: 'Florida requires HIV/AIDS training for health care workers',
    severity: 'medium',
    regulationCode: '381.0035',
    regulationName: 'HIV/AIDS Training',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Have ALL home health aides and clinical staff completed Florida\'s required HIV/AIDS education (minimum 1 hour)?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff completed' },
          { value: 'some', label: 'Some have completed' },
          { value: 'no', label: 'No HIV/AIDS training provided' },
        ],
        required: true,
        helpText: 'Florida requires HIV/AIDS education for health care workers per 381.0035 F.S.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIV/AIDS training is complete for all staff per 381.0035 F.S.',
      atRisk: 'Not all staff have completed HIV/AIDS training. Complete training for remaining staff.',
      nonCompliant: 'Staff are working without required HIV/AIDS training. This violates 381.0035 F.S.',
      unknown: 'Verify HIV/AIDS training requirements under 381.0035 F.S.',
    },
    stateNotes: [
      'Minimum 1 hour HIV/AIDS education required per 381.0035 F.S.',
      'Required for all health care workers in Florida',
      'Training must cover HIV transmission, prevention, and infection control',
      'Many online courses available meet Florida requirements',
      'Document HIV/AIDS training completion in personnel file',
    ],
  },

  {
    id: 'FL-E6',
    itemId: 'E6',
    stateCode: 'FL',
    title: 'Medication Administration Training',
    description: 'Florida requirements for medication assistance/administration training',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Medication Training',
    agencyName: 'Florida Board of Nursing',
    agencyAbbr: 'FL BON',
    agencyPhone: '(850) 488-0595',
    agencyUrl: 'https://floridasnursing.gov',
    questions: [
      {
        id: 'q1',
        question: 'Do your home health aides assist with self-administered medications?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Have all HHAs who assist with medications completed the required 2-hour training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have completed 2-hour medication training' },
          { value: 'some', label: 'Some have completed' },
          { value: 'no', label: 'No medication training provided' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 59A-8.0095(5)(d)15, HHAs must complete 2 hours of training before assisting with self-administered medications',
      },
      {
        id: 'q3',
        question: 'Do any HHAs/CNAs administer medications (not just assist with self-administration)?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q4',
        question: 'Have those administering medications completed the 6-hour medication training and validation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, per 59A-8.0097 F.A.C.' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q3',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 59A-8.0097, medication administration requires 6 hours training plus RN validation',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: Assist without any training
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: Administer medications without 6-hour training
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'no' },
      ], result: 'NON_COMPLIANT' },
      // AT_RISK: Only some staff have assistance training
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'some' },
      ], result: 'AT_RISK' },
      // COMPLIANT: Don't assist AND don't administer
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      // COMPLIANT: Don't assist but administer with proper 6-hour training
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
      // COMPLIANT: Assist with proper 2-hour training, don't administer
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'COMPLIANT' },
      // COMPLIANT: Both assist AND administer with all proper training
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
        { condition: 'equals', questionId: 'q4', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Medication training meets Florida requirements per 59A-8.0095(5)(d)15 and 59A-8.0097 F.A.C.',
      atRisk: 'Some staff may need medication training. Assistance: 2 hours. Administration: 6 hours with RN validation.',
      nonCompliant: 'Staff are assisting/administering medications without required training. This violates Florida law.',
      unknown: 'Review medication training requirements.',
    },
    stateNotes: [
      'Medication ASSISTANCE: 2 hours training per 59A-8.0095(5)(d)15 F.A.C.',
      'Medication ADMINISTRATION: 6 hours training + RN validation per 59A-8.0097 F.A.C.',
      'Assistance = helping patient with self-administered medications',
      'Administration = actually giving medications (requires additional training)',
      'RN must validate competency before HHA can administer medications',
    ],
  },

  {
    id: 'FL-E7',
    itemId: 'E7',
    stateCode: 'FL',
    title: 'Training Documentation',
    description: 'Documentation and retention of employee training records',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Training Records',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain training records for all employees?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, complete records for all employees' },
          { value: 'some', label: 'Some records are incomplete' },
          { value: 'no', label: 'No training record system' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What training documentation do you maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'certs', label: 'Copies of certificates and certifications' },
          { value: 'dates', label: 'Training completion dates' },
          { value: 'hours', label: 'Training hours tracking' },
          { value: 'tests', label: 'Test scores/competency results' },
          { value: 'sign', label: 'Signed training acknowledgments' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT must be first - no training records is a serious violation
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // AT_RISK: incomplete records or insufficient documentation
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'countLessThan', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      // COMPLIANT: complete records with comprehensive documentation
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Training documentation is comprehensive per 59A-8.0095 F.A.C.',
      atRisk: 'Training documentation may be incomplete. Maintain all required training records.',
      nonCompliant: 'No training record system in place. This violates 59A-8.0095 F.A.C.',
      unknown: 'Review training documentation requirements per 59A-8.0095 F.A.C.',
    },
    stateNotes: [
      'Training records must be maintained in personnel file per 59A-8.0095 F.A.C.',
      'Include certificates, completion dates, hours, and test scores',
      'Retain training records for duration of employment plus 5 years',
      'Training records must be available for AHCA survey review',
      'Track expiration dates for CPR, certifications, and annual training',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'FL-F1',
    itemId: 'F1',
    stateCode: 'FL',
    title: 'Plan of Care',
    description: 'Florida requires individualized plan of care for each patient',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Care Plan',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you have an individualized plan of care for EVERY patient?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients have POC' },
          { value: 'most', label: 'Most patients have POC' },
          { value: 'no', label: 'No formal POC process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are plans of care reviewed and updated at least every 60 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care are in place and reviewed per 59A-8.0095 F.A.C.',
      atRisk: 'POC process needs improvement. Florida requires POC review every 60 days.',
      nonCompliant: 'Services are being provided without plans of care. This violates 59A-8.0095 F.A.C.',
      unknown: 'Review POC process against 59A-8.0095 F.A.C. requirements.',
    },
    stateNotes: [
      'Individualized plan of care required for each patient per 59A-8.0095 F.A.C.',
      'POC must be reviewed and updated at least every 60 days per 59A-8.0095 F.A.C.',
      'Physician orders required for skilled services per 59A-8.0095 F.A.C.',
      'POC must include specific services, frequency, and goals',
      'Patient/family must be involved in care planning per 400.487 F.S.',
    ],
  },

  {
    id: 'FL-F3',
    itemId: 'F3',
    stateCode: 'FL',
    title: 'Electronic Visit Verification (EVV)',
    description: 'Florida requires EVV with 85% compliance for Medicaid services',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com/Medicaid/evv',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay/Medicare only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your current EVV compliance rate?',
        inputType: 'select',
        options: [
          { value: '85plus', label: '85% or higher (meets requirement)' },
          { value: '70to84', label: '70-84%' },
          { value: 'below70', label: 'Below 70%' },
          { value: 'no_evv', label: 'No EVV system in use' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Florida requires minimum 85% EVV compliance for Medicaid services',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: '85plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: '70to84', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q2', value: ['below70', 'no_evv'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV compliance meets Florida\'s 85% requirement.',
      atRisk: 'EVV compliance is below the 85% requirement. Improve compliance to avoid claim issues.',
      nonCompliant: 'EVV compliance is significantly below requirements or no EVV in use. Florida Medicaid requires 85% EVV compliance. Contact AHCA EVV unit immediately.',
      unknown: 'If providing Medicaid services, verify EVV compliance rate.',
    },
    stateNotes: [
      'EVV required for Medicaid personal care services per 21st Century Cures Act',
      'Florida requires minimum 85% EVV compliance for claims payment',
      'EVV must capture location, date, time, and service type',
      'Use Florida-approved EVV system or integrate with state aggregator',
      'Non-compliance may result in claim denials and provider sanctions',
    ],
  },

  {
    id: 'FL-F4',
    itemId: 'F4',
    stateCode: 'FL',
    title: 'Supervisory Visits',
    description: 'Florida requires regular supervisory visits for home health aides',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Supervisory Visits',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you conduct supervisory visits for home health aides?',
        inputType: 'select',
        options: [
          { value: 'scheduled', label: 'Yes, on a regular schedule' },
          { value: 'occasional', label: 'Occasionally' },
          { value: 'no', label: 'No supervisory visits' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often are supervisory visits conducted?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'Every 14 days (Medicare/Medicaid requirement per 42 CFR 484.80)' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'every_60', label: 'Every 60 days' },
          { value: 'per_plan', label: 'Based on patient plan of care (state-licensed agencies)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'scheduled',
        },
        helpText: 'Medicare/Medicaid certified agencies must conduct supervisory assessments every 14 days per federal law',
      },
      {
        id: 'q3',
        question: 'Who conducts supervisory visits?',
        inputType: 'select',
        options: [
          { value: 'rn', label: 'RN' },
          { value: 'lpn', label: 'LPN under RN direction' },
          { value: 'therapist', label: 'Licensed therapist (when only therapy is furnished)' },
          { value: 'other', label: 'Other/Unlicensed staff' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'scheduled',
        },
        helpText: 'Per 59A-8.0095 F.A.C., HHA services must be supervised by RN (or therapist when only therapy is provided)',
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: No supervisory visits
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: Occasional visits (Florida requires REGULAR supervision)
      { condition: 'equals', questionId: 'q1', value: 'occasional', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: Unlicensed staff conducting supervision
      { condition: 'equals', questionId: 'q3', value: 'other', result: 'NON_COMPLIANT' },
      // AT_RISK: Every 30 days (does not meet Medicare/Medicaid 14-day requirement per 42 CFR 484.80)
      { condition: 'equals', questionId: 'q2', value: 'every_30', result: 'AT_RISK' },
      // AT_RISK: Every 60 days (significantly below Medicare requirements)
      { condition: 'equals', questionId: 'q2', value: 'every_60', result: 'AT_RISK' },
      // COMPLIANT: Regular schedule with 14-day frequency or per plan of care, with proper supervisor
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'scheduled' },
        { condition: 'includes', questionId: 'q2', value: ['every_14', 'per_plan'] },
        { condition: 'includes', questionId: 'q3', value: ['rn', 'lpn', 'therapist'] },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Supervisory visit schedule meets requirements. Medicare/Medicaid: 42 CFR 484.80. Florida: 59A-8.0095 F.A.C.',
      atRisk: 'Supervisory visits may not meet Medicare/Medicaid 14-day requirement. Review frequency if certified.',
      nonCompliant: 'No regular supervisory visits or supervision by unqualified staff. This violates 59A-8.0095 F.A.C.',
      unknown: 'Review supervisory visit requirements per 59A-8.0095 F.A.C.',
    },
    stateNotes: [
      'Medicare/Medicaid agencies: supervisory visits every 14 days per 42 CFR 484.80',
      'Supervision must be by RN or therapist when only therapy furnished per 59A-8.0095 F.A.C.',
      'LPN may supervise under RN direction per 59A-8.0095 F.A.C.',
      'Document supervisory visits in patient record with findings and recommendations',
      'State-licensed only: supervision per plan of care requirements',
    ],
  },

  {
    id: 'FL-F5',
    itemId: 'F5',
    stateCode: 'FL',
    title: 'Patient Rights',
    description: 'Florida requires informing patients of their rights',
    severity: 'high',
    regulationCode: '400.487',
    regulationName: 'Patient Rights',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(888) 419-3456',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide patients with a copy of their rights?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all patients receive written copy' },
          { value: 'verbal', label: 'Verbal explanation only' },
          { value: 'no', label: 'No patient rights provided' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When are patient rights provided?',
        inputType: 'select',
        options: [
          { value: 'admission', label: 'At admission/start of service' },
          { value: 'later', label: 'Sometime after services begin' },
          { value: 'request', label: 'Only upon request' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
      {
        id: 'q3',
        question: 'Do patients sign acknowledgment of receiving their rights?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: No patient rights provided
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // NON_COMPLIANT: No signed acknowledgment (violates 42 CFR 484.50 for Medicare/Medicaid certified agencies)
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      // AT_RISK: Verbal only, provided late, or only on request
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'verbal' },
        { condition: 'equals', questionId: 'q2', value: 'later' },
        { condition: 'equals', questionId: 'q2', value: 'request' },
      ], result: 'AT_RISK' },
      // COMPLIANT: Written copy at admission with signed acknowledgment
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'admission' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights notification meets Florida requirements per 400.487 F.S. and 42 CFR 484.50.',
      atRisk: 'Patient rights process needs improvement. Provide written copy at admission.',
      nonCompliant: 'Patient rights violation. 42 CFR 484.50 requires signed acknowledgment of receipt.',
      unknown: 'Review patient rights requirements under 400.487 F.S.',
    },
    stateNotes: [
      'Written patient rights must be provided at admission per 400.487 F.S.',
      'Patient rights listed in 400.487 F.S. include dignity, privacy, grievance filing',
      'Signed acknowledgment required and must be retained in patient record',
      'Rights must be provided in language patient understands',
      'Include AHCA complaint hotline number: 1-888-419-3456',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION
  // ============================================
  {
    id: 'FL-G1',
    itemId: 'G1',
    stateCode: 'FL',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Record Maintenance',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'license', label: 'AHCA license (annual renewal)' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'bond', label: 'Surety bond (if applicable)' },
          { value: 'level2', label: 'Level 2 background screenings (5-year renewal)' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive.',
      atRisk: 'Expiration tracking is incomplete. Missing: {missing}.',
      nonCompliant: 'No systematic document tracking in place. This creates compliance risk for license renewal, background screenings, and training.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'AHCA license renewal due 60 days before expiration per 59A-8.003 F.A.C.',
      'Level 2 background screening expires after 5 years per 435.12 F.S.',
      'CPR certifications typically expire every 2 years',
      'Track insurance renewal dates to avoid coverage gaps',
      'Implement 30-60 day advance alerts for all expiring documents',
    ],
  },

  {
    id: 'FL-G2',
    itemId: 'G2',
    stateCode: 'FL',
    title: 'Patient Clinical Records',
    description: 'Florida requires comprehensive clinical documentation for patients',
    severity: 'high',
    regulationCode: '59A-8.0095',
    regulationName: 'Clinical Records',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'Which clinical documents do you maintain for each patient? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'poc', label: 'Plan of care' },
          { value: 'assessment', label: 'Initial and ongoing assessments' },
          { value: 'notes', label: 'Visit notes/progress notes' },
          { value: 'orders', label: 'Physician orders' },
          { value: 'meds', label: 'Medication list' },
          { value: 'consent', label: 'Signed consent forms' },
          { value: 'discharge', label: 'Discharge summary (when applicable)' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are clinical records completed in a timely manner?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Same day or within 24 hours' },
          { value: 'week', label: 'Within a week' },
          { value: 'delayed', label: 'Often delayed' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'equals', questionId: 'q2', value: 'same_day' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'week' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation is comprehensive per 59A-8.0095 F.A.C.',
      atRisk: 'Clinical documentation may be incomplete or delayed. Complete records within 24 hours.',
      nonCompliant: 'Clinical documentation is insufficient. This violates 59A-8.0095 F.A.C.',
      unknown: 'Review clinical documentation requirements per 59A-8.0095 F.A.C.',
    },
    stateNotes: [
      'Clinical records must be maintained for each patient per 59A-8.0095 F.A.C.',
      'Visit notes should be completed within 24 hours of service delivery',
      'Records must include POC, assessments, orders, and progress notes',
      'Records must be accessible at licensed location per 59A-8.0095 F.A.C.',
      'AHCA surveyors will review clinical records during inspections',
    ],
  },

  {
    id: 'FL-G3',
    itemId: 'G3',
    stateCode: 'FL',
    title: 'HIPAA Compliance',
    description: 'Federal HIPAA requirements for patient privacy and security',
    severity: 'high',
    regulationCode: '45 CFR 164',
    regulationName: 'HIPAA',
    agencyName: 'Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa',
    questions: [
      {
        id: 'q1',
        question: 'Do you have written HIPAA policies and procedures?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which HIPAA elements are in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'npp', label: 'Notice of Privacy Practices provided to patients' },
          { value: 'training', label: 'Staff HIPAA training' },
          { value: 'baa', label: 'Business Associate Agreements with vendors' },
          { value: 'security', label: 'Security safeguards for electronic PHI' },
          { value: 'breach', label: 'Breach notification procedures' },
          { value: 'officer', label: 'Designated Privacy/Security Officer' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA compliance program is comprehensive.',
      atRisk: 'HIPAA program may need improvement. Ensure all required elements are in place.',
      nonCompliant: 'No HIPAA policies in place. This violates federal law. Implement HIPAA program immediately.',
      unknown: 'Review HIPAA requirements at hhs.gov/hipaa.',
    },
    stateNotes: [
      'HIPAA Privacy Rule applies to all protected health information per 45 CFR 164',
      'Notice of Privacy Practices must be provided to patients at first visit',
      'Business Associate Agreements required with all vendors handling PHI',
      'Breach notification required within 60 days per 45 CFR 164.404',
      'Florida also has state privacy laws per 395.3025 F.S. and 456.057 F.S.',
    ],
  },

  {
    id: 'FL-G4',
    itemId: 'G4',
    stateCode: 'FL',
    title: 'Record Retention',
    description: 'Florida requirements for retaining patient and business records',
    severity: 'high',
    regulationCode: '400.491',
    regulationName: 'Record Retention',
    agencyName: 'Agency for Health Care Administration',
    agencyAbbr: 'AHCA',
    agencyPhone: '(850) 412-4403',
    agencyUrl: 'https://ahca.myflorida.com',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient clinical records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or more after discharge' },
          { value: '5_7', label: '5-7 years' },
          { value: 'less5', label: 'Less than 5 years' },
        ],
        required: true,
        helpText: 'Florida requires patient records be retained for at least 6 years (longer for minors)',
      },
      {
        id: 'q2',
        question: 'How are records stored?',
        inputType: 'select',
        options: [
          { value: 'secure_electronic', label: 'Secure electronic system with backup' },
          { value: 'electronic', label: 'Electronic system only' },
          { value: 'paper', label: 'Paper records in locked storage' },
          { value: 'unsecured', label: 'Unsecured storage' },
        ],
      },
      {
        id: 'q3',
        question: 'Do you have a record destruction policy?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, with secure destruction methods' },
          { value: 'no', label: 'No formal policy' },
        ],
      },
    ],
    scoringRules: [
      // NON_COMPLIANT: Less than 5 years (violates 6-year rule) OR unsecured storage
      { condition: 'equals', questionId: 'q1', value: 'less5', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'unsecured', result: 'NON_COMPLIANT' },
      // COMPLIANT: 7+ years AND secure storage AND has destruction policy
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '7plus' },
        { condition: 'notEquals', questionId: 'q2', value: 'unsecured' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      // AT_RISK: 5-7 years OR no destruction policy
      { condition: 'equals', questionId: 'q1', value: '5_7', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets Florida requirements per 400.491 F.S.',
      atRisk: 'Record retention or storage may need improvement. Use secure electronic storage with backup and retain records for at least 6 years.',
      nonCompliant: 'Record retention is insufficient or records are unsecured. This violates 400.491 F.S.',
      unknown: 'Review record retention requirements under 400.491 F.S.',
    },
    stateNotes: [
      'Patient records must be retained for 6 years per 400.491 F.S.',
      'Minor patient records: retain until age 25 or 6 years after discharge, whichever is later',
      'Personnel records: retain for 5 years after employment ends per 59A-8.0095 F.A.C.',
      'Secure storage required - electronic with backup or locked physical storage',
      'Record destruction must use secure methods (shredding, secure deletion)',
    ],
  },
];

export default FLORIDA_QUESTIONS;